<!-- last modified by AI tools on 2026-09-30 -->
<script setup lang="ts">
/**
 * ElectionDetailsMobile
 *
 * Mobile (<1024px) election details panel rendered as a bottom Drawer.
 * Uses compact CandidateRow layout, simplified single-line header,
 * and metadata pushed into scrollable content area.
 *
 * @see Design: docs/plans/2026-02-27-refactor-election-details-mobile-panel-design.md
 */
import { inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import Drawer from 'primevue/drawer';
import Button from 'primevue/button';
import Dropdown from 'primevue/dropdown';
import SelectButton from 'primevue/selectbutton';
import ProgressSpinner from 'primevue/progressspinner';
import CandidateRow from './CandidateRow.vue';
import CandidateCard from './CandidateCard.vue';
import CandidateAvatar from '@/components/CandidateAvatar.vue';
import ElectionChart from './ElectionChart.vue';
import ChartPeriodSelector from './ChartPeriodSelector.vue';
import ElectionActivity from './ElectionActivity.vue';
import ElectionNews from './ElectionNews.vue';
import LoginCTA from './LoginCTA.vue';
import type { ForecastTraderElectionMapProduct } from '@/types/forecasttrader.types';
import type { UseElectionDetailsStateOptions } from '../composables/useElectionDetailsState';
import { useElectionDetailsState } from '../composables/useElectionDetailsState';
import { useUserState } from '@/stores/useUserState';
import { isPartyStrikeName } from '../utils/partyUtils';
import { formatCompactNumber } from '@/utils/formatNumber';
import ExchangeAvatarGroup from '@/components/ExchangeAvatarGroup.vue';
import BallotpediaLink from './BallotpediaLink.vue';
import { useNavigation } from '@/composables/useNavigation';
import {
  buildPredictionMarketsUrl,
  ELECTION_BOARD_TAB_KEY,
  type ElectionBoardView,
} from '../utils/predictionMarketsNavigation';
import PredictionMarketsCta from './PredictionMarketsCta.vue';

const userState = useUserState();
const { t } = useI18n();

const props = defineProps<{
  visible: boolean;
  products: ForecastTraderElectionMapProduct[];
  initialProductConid?: number;
  initialCandidateConid?: number;
  /** Leading party from API ('Democrat', 'Republican', etc.) */
  party?: string;
  /** Color intensity from API (0=toss-up, 1=lean, 2=likely, 3=solid) */
  color?: number;
}>();

const emit = defineEmits<{
  'update:visible': [value: boolean];
  /** Share this election. When `conid` is provided, share targets a single candidate; otherwise share is product-level. */
  share: [conid?: number];
  toggleWatchlist: [];
  showGrid: [options?: { filters?: string }];
}>();

const options: UseElectionDetailsStateOptions = {
  products: () => props.products,
  initialProductConid: () => props.initialProductConid,
  initialCandidateConid: () => props.initialCandidateConid,
  visible: () => props.visible,
  party: () => props.party,
  color: () => props.color,
};

const state = useElectionDetailsState(options);
const route = useRoute();
const { openUrl } = useNavigation();
const electionBoardTab = inject(ELECTION_BOARD_TAB_KEY, undefined);

function openPredictionMarkets() {
  const productConid = state.activeProductConid.value;
  const exchange = state.marketInfo.value?.exchange;
  if (!productConid || !exchange) return;
  const origin = state.viewMode.value === 'single-candidate' ? 'single' : 'multi';
  const view: ElectionBoardView = route.meta.viewMode === 'dashboard' ? 'map' : 'grid';
  openUrl(
    buildPredictionMarketsUrl({
      productConid,
      exchange,
      origin,
      view,
      tab: electionBoardTab?.value,
      candidateConid: origin === 'single' ? (state.selectedCandidateConid.value ?? undefined) : undefined,
    }),
  );
}

const drawerProps = {
  position: 'bottom' as const,
  modal: true,
  dismissable: true,
  blockScroll: true,
  showCloseIcon: false,
  style: { width: '100%', maxWidth: '900px', height: '85vh' },
  pt: {
    mask: 'eh-panel-zindex',
    root: 'ratio-design-provider ft2-drawer',
    content: '!h-full flex flex-col !p-0',
  },
};

function closeDrawer() {
  emit('update:visible', false);
}

function handleShare(conid?: number) {
  emit('share', conid);
}
</script>

<template>
  <Drawer :visible="props.visible" v-bind="drawerProps" @update:visible="emit('update:visible', $event)">
    <!-- ─── Header: badge + title/dropdown + actions, OI sub-row ─── -->
    <template #header>
      <div class="flex w-full flex-col gap-1.5 py-1 overscroll-contain">
        <!-- Main row: badge + title/dropdown + share + close -->
        <div class="flex items-center gap-3">
          <div
            v-if="state.electionContract.value"
            class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-sm font-black text-white"
            :style="{ backgroundColor: state.badgeColor.value }"
          >
            {{ state.electionContract.value.stateCode }}
          </div>
          <div class="min-w-0 flex-1">
            <!-- Title becomes a product dropdown when multiple products exist -->
            <Dropdown
              v-if="state.availableProducts.value.length > 1"
              v-model="state.selectedProduct.value"
              :options="state.availableProducts.value"
              optionLabel="name"
              :placeholder="t('selectProduct')"
              class="w-full"
              :pt="{
                root: '!rounded-xl !text-lg !h-auto !shadow-none !px-2 !py-1 !bg-surface !border-surface-200 dark:!border-surface-700',
                input: '!text-lg !font-black !py-0 !px-0 !bg-transparent truncate',
                trigger: '!w-7 !text-on-surface',
                panel: '!text-sm',
                item: '!text-sm !py-2',
              }"
            />
            <p v-else class="text-lg font-black leading-tight">
              {{ state.marketInfo.value?.name || state.electionContract.value?.name || t('electionDetails') }}
            </p>
          </div>
          <div class="flex shrink-0 items-center gap-0.5">
            <Button
              icon="ibi-share-send"
              severity="secondary"
              variant="text"
              rounded
              :pt="{ icon: '!text-xl !text-on-surface-secondary' }"
              @click="handleShare()"
            />
            <Button
              icon="ibi-close"
              severity="secondary"
              variant="text"
              rounded
              :pt="{ icon: '!text-xl !text-on-surface-secondary' }"
              @click="closeDrawer"
            />
          </div>
        </div>
        <!-- Sub-row: Open Interest -->
        <div
          v-if="state.electionContract.value"
          class="flex items-center gap-2 pl-[56px] text-sm font-bold text-muted-color"
        >
          <span>{{ formatCompactNumber(state.electionContract.value.openInterest) }} {{ t('openInterest') }}</span>
        </div>
      </div>
    </template>

    <!-- ─── Loading ─── -->
    <div v-if="state.isLoadingContracts.value" class="flex flex-1 items-center justify-center p-8">
      <div class="text-center">
        <ProgressSpinner class="!h-8 !w-8" />
        <p class="mt-4 text-sm text-muted-color">{{ t('loadingElectionData') }}</p>
      </div>
    </div>

    <!-- ─── Error ─── -->
    <div v-else-if="state.contractError.value" class="flex flex-1 items-center justify-center p-8">
      <div class="text-center">
        <i class="ibi-warning text-4xl text-muted-color" />
        <p class="mt-4 text-base font-medium">{{ t('unableToLoadElectionData') }}</p>
        <p class="mt-2 text-sm text-muted-color">{{ state.contractError.value.message }}</p>
        <button
          class="mt-4 rounded-lg bg-surface-container px-4 py-2 text-sm font-medium hover:bg-surface-container-high"
          @click="state.selectedProduct.value = state.availableProducts.value[0]"
        >
          {{ t('tryAgain') }}
        </button>
      </div>
    </div>

    <!-- ─── Main content ─── -->
    <div
      v-else-if="state.electionContract.value && state.actualCandidates.value"
      class="flex-1 overflow-y-auto overscroll-contain px-4 pt-2 pb-4"
    >
      <LoginCTA class="mb-4" />

      <!-- ═══ Multi-Candidate View ═══ -->
      <div v-if="state.viewMode.value === 'multi-candidate'" class="space-y-3">
        <!-- Compact candidate rows -->
        <div class="space-y-2">
          <CandidateRow
            v-for="candidate in state.filteredCandidates.value?.candidates || []"
            :key="candidate.conid"
            :candidate="candidate"
            :clickable="!state.hasSingleCandidate.value"
            @select="state.handleCandidateSelect"
          />
        </div>

        <PredictionMarketsCta v-if="state.hasMoreContracts.value" @click="openPredictionMarkets" />

        <!-- Price Momentum Chart -->
        <div class="rounded-2xl bg-surface-container p-4 overflow-hidden">
          <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div class="flex items-center gap-2">
              <p class="text-base font-black">{{ t('priceMarketMomentum') }}</p>
              <SelectButton
                v-model="state.chartMode.value"
                :options="[...state.chartModeOptions]"
                option-label="label"
                option-value="value"
                size="small"
                :pt="{
                  root: 'border-0 bg-surface-200/50 rounded-lg p-0.5 bg-surface-high',
                  button: 'px-2.5 py-1 text-[9px] font-black uppercase tracking-wider rounded-md',
                }"
              />
            </div>
            <div class="flex flex-wrap items-center gap-3">
              <div
                v-if="state.filteredCandidates.value?.candidates?.some((c) => c.party === 'DEM')"
                class="flex items-center gap-1.5"
              >
                <div class="h-2.5 w-2.5 shrink-0 rounded-full bg-democrat" />
                <span class="text-[10px] font-bold uppercase text-muted-color">{{ t('dem') }}</span>
              </div>
              <div
                v-if="state.filteredCandidates.value?.candidates?.some((c) => c.party === 'GOP')"
                class="flex items-center gap-1.5"
              >
                <div class="h-2.5 w-2.5 shrink-0 rounded-full bg-republican" />
                <span class="text-[10px] font-bold uppercase text-muted-color">{{ t('gop') }}</span>
              </div>
              <div
                v-if="state.filteredCandidates.value?.candidates?.some((c) => c.party === 'IND')"
                class="flex items-center gap-1.5"
              >
                <div class="h-2.5 w-2.5 shrink-0 rounded-full bg-independent" />
                <span class="text-[10px] font-bold uppercase text-muted-color">{{ t('ind') }}</span>
              </div>
            </div>
          </div>

          <ChartPeriodSelector
            v-model="state.chartPeriod.value"
            :options="[...state.chartPeriodOptions]"
            class="mb-2"
          />

          <div class="h-[280px] w-full overflow-hidden">
            <ElectionChart
              :conids="state.chartConids.value"
              :series-colors="state.chartColors.value"
              :chart-type="state.chartMode.value === 'line' ? 'Line' : 'Area'"
              :chart-height="280"
              :period="state.chartPeriodConfig.value.period"
              :step="state.chartPeriodConfig.value.step"
            />
          </div>
        </div>
      </div>

      <!-- ═══ Single Candidate View ═══ -->
      <div v-else-if="state.viewMode.value === 'single-candidate'" class="space-y-4">
        <!-- Loading -->
        <div v-if="state.isLoadingDetails.value" class="flex items-center justify-center py-8">
          <div class="text-center">
            <ProgressSpinner class="!h-8 !w-8" />
            <p class="mt-4 text-sm text-muted-color">{{ t('loading') }}</p>
          </div>
        </div>

        <!-- Error -->
        <div v-else-if="state.detailsError.value" class="text-center py-8">
          <p class="text-base font-medium">{{ t('unableToLoadCandidateDetails') }}</p>
          <button
            class="mt-4 rounded-lg bg-surface-container px-4 py-2 text-sm font-medium hover:bg-surface-container-high"
            @click="state.handleBackToMultiView"
          >
            {{ t('backToCandidates') }}
          </button>
        </div>

        <!-- Single candidate content -->
        <div v-else-if="state.candidateDetails.value">
          <!-- Back button -->
          <button
            class="mb-4 flex items-center gap-1 rounded-full bg-surface-container-high px-3 py-1.5 text-xs font-bold uppercase tracking-wider"
            @click="state.handleBackToMultiView"
          >
            <i class="ibi-arrow-left text-sm" />
            {{ t('back') }}
          </button>

          <!-- Find selected candidate -->
          <template v-for="candidate in state.actualCandidates.value.candidates" :key="candidate.conid">
            <div v-if="candidate.conid === state.selectedCandidateConid.value" class="space-y-4">
              <!-- Candidate card with smaller avatar for mobile -->
              <div
                :class="[
                  'flex flex-col rounded-2xl border p-4',
                  candidate.party === 'DEM' && 'border-democrat-subtle bg-democrat-subtle/20',
                  candidate.party === 'GOP' && 'border-republican-subtle bg-republican-subtle/20',
                  candidate.party === 'IND' && 'border-independent-subtle bg-independent-subtle/20',
                  candidate.party === 'GRN' && 'border-green-subtle bg-green-subtle/20',
                ]"
              >
                <!-- Header row: exchange icons (left) | avatar (center) | spacer (right) -->
                <div class="flex w-full items-start justify-center gap-4 mb-4">
                  <div class="flex flex-1 items-center min-w-0">
                    <ExchangeAvatarGroup :exchanges="candidate.exchanges" :exchange="candidate.exchange" size="small" />
                  </div>
                  <div class="relative shrink-0">
                    <div
                      :class="[
                        'h-20 w-20 overflow-hidden rounded-2xl ring-4 ring-offset-3',
                        candidate.party === 'DEM' && 'ring-democrat-subtle',
                        candidate.party === 'GOP' && 'ring-republican-subtle',
                        candidate.party === 'IND' && 'ring-independent-subtle',
                        candidate.party === 'GRN' && 'ring-green-subtle',
                      ]"
                    >
                      <CandidateAvatar
                        :conid="candidate.conid"
                        :candidate-name="candidate.name"
                        :party-code="candidate.party"
                        size="fill"
                      />
                    </div>
                    <div
                      v-if="!isPartyStrikeName(candidate.name)"
                      :class="[
                        'absolute -bottom-1 -right-1 rounded-md px-2 py-0.5 text-[8px] font-black uppercase text-white',
                        candidate.party === 'DEM' && 'bg-democrat',
                        candidate.party === 'GOP' && 'bg-republican',
                        candidate.party === 'IND' && 'bg-independent',
                        candidate.party === 'GRN' && 'bg-green',
                      ]"
                    >
                      {{ candidate.party }}
                    </div>
                    <div
                      v-if="candidate.isIncumbent"
                      class="absolute -left-1 -top-1 rounded-md bg-surface-inverse px-1.5 py-0.5 text-[7px] font-black uppercase text-on-surface-inverse shadow-lg"
                    >
                      {{ t('incumbent') }}
                    </div>
                  </div>
                  <div class="flex flex-1 items-center justify-end gap-3 min-w-0">
                    <button
                      class="shrink-0 cursor-pointer text-muted-color hover:text-on-surface transition-colors"
                      title="Share"
                      @click.stop="handleShare(candidate.conid)"
                    >
                      <i class="ibi-share-send text-base" />
                    </button>
                    <BallotpediaLink
                      :name="candidate.name"
                      :aria-label="`Learn more about ${candidate.name} on Ballotpedia`"
                      class="shrink-0 text-muted-color hover:text-on-surface transition-colors"
                      @click.stop
                    >
                      <i class="ibi-arrow-up-right text-base" />
                    </BallotpediaLink>
                  </div>
                </div>
                <p class="mb-1 text-center text-xl font-black">{{ candidate.name }}</p>
                <p
                  v-if="!isPartyStrikeName(candidate.name)"
                  :class="[
                    'mb-4 text-center text-[10px] font-bold uppercase tracking-widest',
                    candidate.party === 'DEM' && 'text-democrat',
                    candidate.party === 'GOP' && 'text-republican',
                    candidate.party === 'IND' && 'text-independent',
                    candidate.party === 'GRN' && 'text-green',
                  ]"
                >
                  {{ candidate.partyAffiliation }}
                </p>
                <CandidateCard :candidate="candidate" class="!border-0 !bg-transparent !p-0" />
              </div>

              <PredictionMarketsCta v-if="state.hasMoreContracts.value" @click="openPredictionMarkets" />

              <!-- Chart -->
              <div class="rounded-2xl bg-surface-container p-4 overflow-hidden">
                <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <div class="flex items-center gap-2">
                    <p class="text-base font-black">{{ t('priceMarketMomentum') }}</p>
                    <SelectButton
                      v-model="state.chartMode.value"
                      :options="[...state.chartModeOptions]"
                      option-label="label"
                      option-value="value"
                      size="small"
                      :pt="{
                        root: 'border-0 bg-surface-200/50 rounded-lg p-0.5 bg-surface-high',
                        button: 'px-2.5 py-1 text-[9px] font-black uppercase tracking-wider rounded-md',
                      }"
                    />
                  </div>
                  <div class="flex items-center gap-1.5">
                    <div
                      :class="[
                        'h-3 w-3 shrink-0 rounded-full',
                        candidate.party === 'DEM' && 'bg-democrat',
                        candidate.party === 'GOP' && 'bg-republican',
                        candidate.party === 'IND' && 'bg-independent',
                        candidate.party === 'GRN' && 'bg-green',
                      ]"
                    />
                    <span class="text-xs font-bold uppercase text-muted-color">
                      {{ candidate.name.split(' ').pop() }}
                    </span>
                  </div>
                </div>
                <ChartPeriodSelector
                  v-model="state.chartPeriod.value"
                  :options="[...state.chartPeriodOptions]"
                  class="mb-2"
                />

                <div class="h-[280px] w-full overflow-hidden">
                  <ElectionChart
                    :conids="state.chartConids.value"
                    :series-colors="state.chartColors.value"
                    :chart-type="state.chartMode.value === 'line' ? 'Line' : 'Area'"
                    :chart-height="280"
                    :period="state.chartPeriodConfig.value.period"
                    :step="state.chartPeriodConfig.value.step"
                  />
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Activity (auth-only) -->
      <ElectionActivity v-if="userState.authenticated" class="mt-4" :conids="state.productConids.value" />

      <!-- Related News (auth-only) -->
      <ElectionNews
        v-if="userState.authenticated && state.activeProductConid.value"
        class="mt-4"
        :conid="state.activeProductConid.value"
      />
    </div>
  </Drawer>
</template>
